export type X57SurfacePoint = readonly [number, number, number];

export interface X57SampledSurface {
  kind: "sampled-grid";
  index: number;
  nu: number;
  nv: number;
  points: X57SurfacePoint[];
}

export interface X57BezierPatch {
  ip: number;
  jp: number;
  du: number;
  dv: number;
  controlPoints: X57SurfacePoint[];
}

export interface X57BezierSurface {
  kind: "exact-bezier";
  index: number;
  nuPatches: number;
  nvPatches: number;
  patches: X57BezierPatch[];
}

export type X57ParameterizedSurface = X57SampledSurface | X57BezierSurface;

export interface X57SurfaceComponent {
  geomId: string;
  name: string;
  surfaces: X57ParameterizedSurface[];
}

export interface X57SurfaceModel {
  mode: "sampled-grid" | "exact-bezier";
  components: X57SurfaceComponent[];
}

export interface X57SurfaceMetadata {
  source: string;
  vspVersion: string | null;
  mode: "sampled-grid" | "exact-bezier" | "tessellated";
  resolution: number | null;
  componentCount: number;
  surfaceOrPatchCount: number;
  triangleCount: number;
  bbox: readonly [X57SurfacePoint, X57SurfacePoint] | null;
  skippedSurfaceCount: number;
  timingsMs: Readonly<Record<string, number>>;
  fixtureNote?: string;
}

export interface X57SurfaceLines {
  parameterLines: Float32Array;
  boundaries: Float32Array;
  parameterLineCount: number;
  boundaryLineCount: number;
  label: string;
}

type JsonRecord = Record<string, unknown>;

function record(value: unknown, path: string): JsonRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as JsonRecord;
}

function finiteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be finite`);
  return value;
}

function positiveInteger(value: unknown, path: string): number {
  const parsed = finiteNumber(value, path);
  if (!Number.isInteger(parsed) || parsed < 1) throw new Error(`${path} must be a positive integer`);
  return parsed;
}

function point(value: unknown, path: string): X57SurfacePoint {
  if (!Array.isArray(value) || value.length !== 3) throw new Error(`${path} must be an xyz triple`);
  return [
    finiteNumber(value[0], `${path}[0]`),
    finiteNumber(value[1], `${path}[1]`),
    finiteNumber(value[2], `${path}[2]`),
  ];
}

function points(value: unknown, count: number, path: string): X57SurfacePoint[] {
  if (!Array.isArray(value) || value.length !== count) throw new Error(`${path} must contain ${count} points`);
  return value.map((item, index) => point(item, `${path}[${index}]`));
}

/** Validate the canonical worker payload before it becomes renderer state. */
export function parseX57SurfaceModel(value: unknown): X57SurfaceModel {
  const root = record(value, "surface model");
  if (root.mode !== "sampled-grid" && root.mode !== "exact-bezier") {
    throw new Error("surface model mode must be sampled-grid or exact-bezier");
  }
  if (!Array.isArray(root.components)) throw new Error("surface model components must be an array");
  const mode = root.mode;
  const components = root.components.map((rawComponent, componentIndex): X57SurfaceComponent => {
    const component = record(rawComponent, `components[${componentIndex}]`);
    if (typeof component.geom_id !== "string" || typeof component.name !== "string") {
      throw new Error(`components[${componentIndex}] must identify a geom and name`);
    }
    if (!Array.isArray(component.surfaces)) throw new Error(`components[${componentIndex}].surfaces must be an array`);
    const surfaces = component.surfaces.map((rawSurface, surfaceIndex): X57ParameterizedSurface => {
      const surface = record(rawSurface, `components[${componentIndex}].surfaces[${surfaceIndex}]`);
      if (mode === "sampled-grid") {
        const nu = positiveInteger(surface.nu, "sampled surface nu");
        const nv = positiveInteger(surface.nw, "sampled surface nw");
        return {
          kind: mode,
          index: positiveInteger(Number(surface.index) + 1, "sampled surface index") - 1,
          nu,
          nv,
          points: points(surface.points, nu * nv, "sampled surface points"),
        };
      }
      if (!Array.isArray(surface.patches)) throw new Error("exact surface patches must be an array");
      return {
        kind: mode,
        index: surfaceIndex,
        nuPatches: positiveInteger(surface.nu_patches, "exact surface nu_patches"),
        nvPatches: positiveInteger(surface.nv_patches, "exact surface nv_patches"),
        patches: surface.patches.map((rawPatch, patchIndex): X57BezierPatch => {
          const patch = record(rawPatch, `exact patch ${patchIndex}`);
          const du = positiveInteger(Number(patch.du) + 1, "exact patch degree u") - 1;
          const dv = positiveInteger(Number(patch.dv) + 1, "exact patch degree v") - 1;
          return {
            ip: finiteNumber(patch.ip, "exact patch ip"),
            jp: finiteNumber(patch.jp, "exact patch jp"),
            du,
            dv,
            controlPoints: points(patch.cp, (du + 1) * (dv + 1), "exact patch control points"),
          };
        }),
      };
    });
    return { geomId: component.geom_id, name: component.name, surfaces };
  });
  return { mode, components };
}

export function parseX57SurfaceMetadata(value: unknown): X57SurfaceMetadata {
  const raw = record(value, "surface metadata");
  const mode = raw.mode === "sampled-grid" || raw.mode === "exact-bezier" ? raw.mode : "tessellated";
  const timings = raw.timings_ms && typeof raw.timings_ms === "object" && !Array.isArray(raw.timings_ms)
    ? Object.fromEntries(Object.entries(raw.timings_ms as JsonRecord)
      .filter((entry): entry is [string, number] => typeof entry[1] === "number" && Number.isFinite(entry[1])))
    : {};
  const bbox = Array.isArray(raw.bbox) && raw.bbox.length === 2
    ? [point(raw.bbox[0], "metadata bbox[0]"), point(raw.bbox[1], "metadata bbox[1]")] as const
    : null;
  return {
    source: typeof raw.source === "string" ? raw.source : "unknown",
    vspVersion: typeof raw.vsp_version === "string" ? raw.vsp_version : null,
    mode,
    resolution: typeof raw.resolution === "number" ? raw.resolution : null,
    componentCount: typeof raw.n_components === "number" ? raw.n_components : 0,
    surfaceOrPatchCount: typeof raw.total_patches === "number" ? raw.total_patches : 0,
    triangleCount: typeof raw.total_triangles === "number" ? raw.total_triangles : 0,
    bbox,
    skippedSurfaceCount: Array.isArray(raw.skipped_surfaces) ? raw.skipped_surfaces.length : 0,
    timingsMs: timings,
  };
}

function appendSegment(target: number[], a: X57SurfacePoint, b: X57SurfacePoint): void {
  target.push(a[0], a[1], a[2], b[0], b[1], b[2]);
}

function appendPolyline(target: number[], values: readonly X57SurfacePoint[]): void {
  for (let index = 0; index + 1 < values.length; index += 1) appendSegment(target, values[index]!, values[index + 1]!);
}

function sampledLines(surface: X57SampledSurface, inner: number[], boundary: number[]): void {
  const at = (u: number, v: number) => surface.points[u * surface.nv + v]!;
  for (let u = 1; u < surface.nu - 1; u += 1) {
    appendPolyline(inner, Array.from({ length: surface.nv }, (_, v) => at(u, v)));
  }
  for (let v = 1; v < surface.nv - 1; v += 1) {
    appendPolyline(inner, Array.from({ length: surface.nu }, (_, u) => at(u, v)));
  }
  appendPolyline(boundary, Array.from({ length: surface.nv }, (_, v) => at(0, v)));
  appendPolyline(boundary, Array.from({ length: surface.nv }, (_, v) => at(surface.nu - 1, v)));
  appendPolyline(boundary, Array.from({ length: surface.nu }, (_, u) => at(u, 0)));
  appendPolyline(boundary, Array.from({ length: surface.nu }, (_, u) => at(u, surface.nv - 1)));
}

function bezierLines(surface: X57BezierSurface, inner: number[], boundary: number[]): void {
  for (const patch of surface.patches) {
    const at = (u: number, v: number) => patch.controlPoints[u * (patch.dv + 1) + v]!;
    for (let u = 1; u < patch.du; u += 1) appendPolyline(inner, Array.from({ length: patch.dv + 1 }, (_, v) => at(u, v)));
    for (let v = 1; v < patch.dv; v += 1) appendPolyline(inner, Array.from({ length: patch.du + 1 }, (_, u) => at(u, v)));
    appendPolyline(boundary, Array.from({ length: patch.dv + 1 }, (_, v) => at(0, v)));
    appendPolyline(boundary, Array.from({ length: patch.dv + 1 }, (_, v) => at(patch.du, v)));
    appendPolyline(boundary, Array.from({ length: patch.du + 1 }, (_, u) => at(u, 0)));
    appendPolyline(boundary, Array.from({ length: patch.du + 1 }, (_, u) => at(u, patch.dv)));
  }
}

export function buildX57SurfaceLines(model: X57SurfaceModel, geomId: string | null): X57SurfaceLines {
  const component = model.components.find((item) => item.geomId === geomId);
  const inner: number[] = [];
  const boundary: number[] = [];
  for (const surface of component?.surfaces ?? []) {
    if (surface.kind === "sampled-grid") sampledLines(surface, inner, boundary);
    else bezierLines(surface, inner, boundary);
  }
  return {
    parameterLines: new Float32Array(inner),
    boundaries: new Float32Array(boundary),
    parameterLineCount: inner.length / 6,
    boundaryLineCount: boundary.length / 6,
    label: model.mode === "sampled-grid" ? "sampled u/v grid" : "Bézier control grid",
  };
}

export function x57ComponentSurfaceStats(model: X57SurfaceModel, geomId: string | null) {
  const component = model.components.find((item) => item.geomId === geomId);
  const surfaces = component?.surfaces ?? [];
  const patches = surfaces.reduce((sum, surface) => sum + (surface.kind === "exact-bezier" ? surface.patches.length : 1), 0);
  const points = surfaces.reduce((sum, surface) => sum + (surface.kind === "sampled-grid"
    ? surface.points.length
    : surface.patches.reduce((count, patch) => count + patch.controlPoints.length, 0)), 0);
  return { component, surfaceCount: surfaces.length, patches, points };
}
